import { useState } from 'react';
import { BarChart3, List, ChevronRight, Plus, Car, Users } from 'lucide-react';
import './DispatcherTimelineBoard.css';
import { scheduleOwner } from './scheduleOwner';
import { getScheduleDurationHours, getScheduleRouteLabel } from './scheduleDuration';
import { assignTimelineLanes } from './timelineLanes';
export { scheduleOwner } from './scheduleOwner';

const CANCELLED = ['cancelled', 'canceled', '已取消'];

function minutes(value) {
  if (!/^\d{1,2}:\d{2}$/.test(value || '')) return null;
  const [hour, minute] = value.split(':').map(Number);
  return hour < 24 && minute < 60 ? hour * 60 + minute : null;
}

export function boardRange(item) {
  const rest = item.timeline_type === 'rest';
  if (rest && item.all_day !== false) return { start: 0, end: 1440 };
  const start = minutes(rest ? item.start_time : item.time);
  if (start === null) return null;
  if (rest) {
    const end = minutes(item.end_time);
    return end !== null && end > start ? { start, end } : null;
  }
  const duration = getScheduleDurationHours(item);
  return { start, end: Math.min(1440, start + duration * 60) };
}

export function placeBoardItems(items) {
  return assignTimelineLanes(items.filter(item => !CANCELLED.includes(item.status)).map(item => ({ item, range: boardRange(item) }))
    .filter(entry => entry.range));
}

function clock(value) {
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
}

function DetailRow({ item, canViewPrice, onOpen }) {
  const owner = scheduleOwner(item);
  return <button type="button" className="dispatch-order-detail" style={{ '--dispatch-owner-color': owner.color }} onClick={() => onOpen(item)}>
    <span className="dispatch-order-time">
      <strong>{item.time || '时间待确认'}</strong>
      <span><Car size={13} aria-hidden="true" />{item.vehicle_type || '车型未设置'}</span>
      <span><Users size={13} aria-hidden="true" />{item.passenger_count == null ? '人数未设置' : `${item.passenger_count}人`}</span>
    </span>
    <span className="dispatch-order-route">
      <span className="dispatch-route-from">{item.pickup_location || item.pickup_area || '出发地未设置'}</span>
      <span className="dispatch-route-to">{item.dropoff_location || item.dropoff_area || '目的地未设置'}</span>
      <span className="dispatch-order-owner" style={{ color: owner.color }}>{owner.label}</span>
      {canViewPrice && item.price_amount != null && <strong className="dispatch-order-price">{Number(item.price_amount).toLocaleString()} {item.price_currency || 'JPY'}</strong>}
      {!canViewPrice && <span className="dispatch-detail-muted">价格无权限</span>}
      {canViewPrice && item.price_amount == null && <span className="dispatch-detail-muted">价格未设置</span>}
    </span>
    <ChevronRight size={16} aria-hidden="true" />
  </button>;
}

export default function DispatcherTimelineBoard({ drivers, days, schedules, getItems, dispatcher, onOpen, onViewAll, onAssign }) {
  const [driverId, setDriverId] = useState('');
  const [mode, setMode] = useState('chart');
  const [period, setPeriod] = useState(3);
  const [query, setQuery] = useState('');
  const shownDrivers = drivers.filter(driver => `${driver.name || ''} ${driver.phone || ''}`.toLowerCase().includes(query.trim().toLowerCase()));
  const selected = shownDrivers.find(driver => driver.id === driverId) || shownDrivers[0];
  const visibleDays = days.slice(0, period);
  const rows = shownDrivers.flatMap(driver => visibleDays.map((day, dayIndex) => ({
    driver, day, dayIndex, entries: placeBoardItems(getItems(schedules[driver.id], day.date)),
  })));
  const timedEntries = rows.flatMap(row => row.entries).filter(entry => !(entry.item.timeline_type === 'rest' && entry.item.all_day !== false));
  const owners = Array.from(new Map(rows.flatMap(row => row.entries).filter(entry => entry.item.timeline_type !== 'rest').map(entry => {
    const owner = scheduleOwner(entry.item);
    return [`${owner.label}:${owner.color}`, owner];
  })).values());
  const start = Math.floor(Math.min(360, ...timedEntries.map(entry => entry.range.start)) / 120) * 120;
  const end = Math.ceil(Math.max(1320, ...timedEntries.map(entry => entry.range.end)) / 120) * 120;
  const span = end - start;
  const ticks = Array.from({ length: Math.floor(span / 240) + 1 }, (_, index) => start + index * 240);
  if (ticks[ticks.length - 1] !== end) ticks.push(end);
  const canViewPrice = Boolean(dispatcher?.permissions?.can_view_price);
  return <section className="dispatch-board" data-testid="dispatcher-driver-availability-board">
    <div className="dispatch-board-toolbar">
      <input aria-label="搜索司机" placeholder="搜索司机" value={query} onChange={event => setQuery(event.target.value)} />
      <div className="dispatch-segments" aria-label="日期范围">{[1, 3].map(value => <button type="button" aria-pressed={period === value} key={value} onClick={() => setPeriod(value)}>{value === 1 ? '当天' : '3天'}</button>)}</div>
      <div className="dispatch-segments"><button type="button" title="图表" aria-label="图表" aria-pressed={mode === 'chart'} onClick={() => setMode('chart')}><BarChart3 size={18} /></button><button type="button" title="列表" aria-label="列表" aria-pressed={mode === 'list'} onClick={() => setMode('list')}><List size={18} /></button></div>
    </div>
    {shownDrivers.length === 0 && <p className="dispatch-empty">没有匹配的司机</p>}
    {mode === 'chart' && <div className="dispatch-day">
        <div className="dispatch-timeline-scroll" tabIndex={0} aria-label="司机工作与休息时间轴">
          <div className="dispatch-timeline-grid">
            <div className="dispatch-axis"><span className="dispatch-driver-cell">司机</span><div>{ticks.map(tick => <span key={tick} style={{ left: `${(tick - start) / span * 100}%` }}>{tick / 60}</span>)}</div></div>
            {rows.map(({ driver, day, dayIndex, entries }) => <div className={`dispatch-timeline-row ${dayIndex === 0 ? 'dispatch-driver-start' : ''}`} key={`${driver.id}-${day.date}`} data-driver-id={driver.id} data-date={day.date} style={{ '--row-height': `${Math.max(80, (Math.max(0, ...entries.map(entry => entry.lane)) + 1) * 64 + 16)}px` }}>
              <button className={`dispatch-driver-cell ${selected?.id === driver.id ? 'is-selected' : ''}`} type="button" onClick={() => setDriverId(driver.id)}><strong>{driver.name}</strong>{period === 3 && <small>{day.date.slice(5)} 周{day.weekday}</small>}</button>
              <div className="dispatch-tracks">
                {ticks.map(tick => <i aria-hidden="true" key={tick} style={{ left: `${(tick - start) / span * 100}%` }} />)}
                {entries.map(({ item, range, lane }, index) => {
                  const rest = item.timeline_type === 'rest';
                  const owner = scheduleOwner(item);
                  const barLabel = rest ? (item.all_day !== false ? '' : `${item.start_time}–${item.end_time}`) : getScheduleRouteLabel(item);
                  const label = rest ? (item.all_day !== false ? '全天休息' : `${clock(range.start)}–${clock(range.end)} 休息`) : `${item.time}–${clock(range.end)} ${item.pickup_location || item.pickup_area || ''} → ${item.dropoff_location || item.dropoff_area || ''} · ${owner.label}`;
                  const left = Math.max(start, range.start);
                  const right = Math.min(end, range.end);
                  return <button key={`${item.id || index}-${index}`} type="button" className="dispatch-timeline-entry" data-lane={lane} style={{ '--dispatch-owner-color': rest ? '#ffb6a5' : owner.color, top: `${8 + lane * 64}px`, left: `${(left - start) / span * 100}%`, width: `${(right - left) / span * 100}%` }} title={label} aria-label={`${driver.name} ${day.date} ${label}`} onClick={() => { setDriverId(driver.id); if (!rest) onOpen(item); }}>
                    <span aria-hidden="true" className={`dispatch-bar ${rest ? 'is-rest' : 'is-work'}`} />
                    <span className="dispatch-mobile-number" aria-hidden="true">{index + 1}</span>
                    <span className="dispatch-bar-caption"><strong>{rest ? (item.all_day !== false ? '全天休息' : '休息') : `${item.time}–${clock(range.end)}`}</strong><span>{barLabel}</span></span>
                  </button>;
                })}
                {entries.length === 0 && <span className="dispatch-track-empty">无已排时段</span>}
              </div>
              <div className="dispatch-mobile-summaries">
                {entries.map(({ item, range }, index) => <button type="button" key={item.id || index} style={{ color: item.timeline_type === 'rest' ? '#ffb6a5' : scheduleOwner(item).color }} onClick={() => { setDriverId(driver.id); if (item.timeline_type !== 'rest') onOpen(item); }}>
                  <b>{index + 1}</b><span>{item.timeline_type === 'rest' ? (item.all_day !== false ? '全天休息' : `${clock(range.start)}–${clock(range.end)} 休息`) : `${clock(range.start)}–${clock(range.end)} ${getScheduleRouteLabel(item)}`}</span>
                </button>)}
              </div>
            </div>)}
          </div>
        </div>
      </div>}
    <div className="dispatch-legend">{owners.map(owner => <span key={`${owner.label}:${owner.color}`} className="owner-key" style={{ '--owner-key-color': owner.color }}>{owner.label}</span>)}<span className="rest-key">休息</span></div>
    {(mode === 'list' ? shownDrivers : selected ? [selected] : []).map(driver => <section className="dispatch-driver-detail" key={driver.id}>
      <header><h3>{driver.name}</h3><button type="button" onClick={() => onViewAll(driver)}>全部排班<ChevronRight size={15} /></button>{dispatcher?.permissions?.can_create && <button type="button" title={`为${driver.name}添加排班`} aria-label={`为${driver.name}添加排班`} onClick={() => onAssign(driver)}><Plus size={18} /></button>}</header>
      {visibleDays.map(day => {
        const items = getItems(schedules[driver.id], day.date).filter(item => !CANCELLED.includes(item.status));
        return <div key={day.date}>
          {period === 3 && <h4>{day.date} · 周{day.weekday}</h4>}
          {items.length === 0 && <p className="dispatch-empty">暂无排班</p>}
          {items.map((item, index) => item.timeline_type === 'rest' ? <div className="dispatch-rest-detail" key={item.id || index}>{item.all_day !== false ? '全天休息' : `${item.start_time || '--:--'}–${item.end_time || '--:--'} 休息`}</div> : <DetailRow key={item.id || index} item={item} canViewPrice={canViewPrice} onOpen={onOpen} />)}
        </div>;
      })}
    </section>)}
  </section>;
}
